/**
 * EditLinkedTable - Main linkedTable editor page
 *
 * Manages the editing and creation of linkedTables with:
 * - TanStack Query for efficient data fetching
 * - Centralized state management via custom hooks
 * - Real-time prelinkedTable of changes
 * - Clean separation of concerns
 */

import React, { useMemo, useEffect, useRef } from 'react';
import type { TableFieldApiResponse } from '@loader/shared-types';
import { isQualifiedFieldId, qualifyFieldId } from '@loader/shared-types';

import type { LinkedTable } from '@/lib/types';
import {
  useLinkedTableState,
  useLinkedTableEditor,
  useLinkedTableMetadata,
  useLinkedTableVersionsQuery,
  useLinkedTableSaveMutation,
  useLinkedTableInitialization,
} from '@/features/linkedTables/editLinkedTable/hooks';
import { LinkedTableDataDisplay } from '@/features/linkedTables/editLinkedTable/components';
import { LinkedTableSidebar } from '@/features/linkedTables/editLinkedTable/components/LinkedTableSidebar';
import { useLinkedTableSidebarStore } from '@/features/linkedTables/editLinkedTable/stores/useLinkedTableSidebarStore';
import { logger } from '@/lib/logger';
import { CALCULATED_COLUMNS_TABLE_ALIAS } from '@/lib/constants';

/**
 * Props for EditLinkedTableContent
 */
interface EditLinkedTableContentProps {
  /** Linked table ID to edit */
  linkedTableId: string;
  /** Whether the user can edit (false for viewers) */
  canEdit?: boolean;
}

/**
 * EditLinkedTableContent - Main component for editing linkedTables
 * Used exclusively within the workspace overlay (ExpandedItemContent).
 */
const EditLinkedTableContent: React.FC<EditLinkedTableContentProps> = ({ linkedTableId, canEdit = true }) => {
  const isCreateMode = !linkedTableId;

  logger.debug('EditLinkedTableContent render', { linkedTableId, isCreateMode });

  // Reset sidebar to "columns" tab when opening a different linked table
  const resetSidebar = useLinkedTableSidebarStore((s) => s.reset);
  useEffect(() => {
    resetSidebar();
  }, [linkedTableId, resetSidebar]);

  // ============================================================================
  // Data Fetching - TanStack Query
  // ============================================================================

  const { linkedTable: loadedLinkedTable } = useLinkedTableMetadata({
    id: linkedTableId,
    enabled: !isCreateMode,
  });

  // ============================================================================
  // State Management
  // ============================================================================

  const linkedTableState = useLinkedTableState(isCreateMode);

  // ============================================================================
  // Table Fields - For sidebar filters, calculated columns, relationships
  // ============================================================================

  // Extract primary table info from tables[0]
  const primaryTable = linkedTableState.linkedTable?.tables?.[0];
  const primaryTableId = primaryTable?.target_id;

  /**
   * All available fields from all tables (primary + relationships)
   * Populated by LinkedTableDataDisplay from preview's available_fields response
   * This includes ALL fields from ALL tables, not just the ones in the query result
   */
  const [availableFields, setAvailableFields] = React.useState<TableFieldApiResponse[]>([]);

  /**
   * Handle available fields update from preview response
   * The preview endpoint returns available_fields which contains ALL fields from ALL tables
   * We exclude calculated columns as they are handled separately via the calculated_columns prop
   */
  const handleFieldsChange = React.useCallback((fields: TableFieldApiResponse[]) => {
    // Filter out calculated columns - they're handled separately
    const nonCalculatedFields = fields.filter(field => {
      return field.table_alias !== CALCULATED_COLUMNS_TABLE_ALIAS;
    });
    setAvailableFields(nonCalculatedFields);
  }, []);

  // ============================================================================
  // LinkedTable Initialization
  // ============================================================================

  useLinkedTableInitialization({
    // Cast to unknown first since API response type differs slightly from local type
    // The initialization function handles the conversion internally
    loadedLinkedTable: (loadedLinkedTable || null) as unknown as LinkedTable | null,
    isCreateMode,
    onInitializeLinkedTable: linkedTableState.initializeFromLinkedTable,
  });

  // ============================================================================
  // Backward-Compatible Qualified Field ID Upgrade
  // ============================================================================

  // When availableFields first populate, upgrade bare field IDs to qualified format.
  // Existing linked tables may store bare UUIDs in row_groups/values/row_group_sorts.
  const hasUpgradedFieldIds = useRef(false);

  useEffect(() => {
    if (hasUpgradedFieldIds.current || availableFields.length === 0) return;
    hasUpgradedFieldIds.current = true;

    // Build bare-ID → qualified-ID map from availableFields
    const bareToQualified = new Map<string, string>();
    for (const field of availableFields) {
      const alias = field.table_alias;
      if (alias) {
        bareToQualified.set(field.id, qualifyFieldId(alias, field.id));
      }
    }

    // Helper: upgrade a field ID if it's bare and has a known alias
    const upgrade = (id: string): string => {
      if (isQualifiedFieldId(id)) return id;
      return bareToQualified.get(id) ?? id;
    };

    // Upgrade row_groups
    const currentRowGroups = linkedTableState.row_groups;
    if (currentRowGroups.some(id => !isQualifiedFieldId(id) && bareToQualified.has(id))) {
      linkedTableState.setRowGroups(currentRowGroups.map(upgrade));
    }

    // Upgrade values
    const currentValues = linkedTableState.values;
    if (currentValues.some(v => !isQualifiedFieldId(v.field_id) && bareToQualified.has(v.field_id))) {
      linkedTableState.setValues(currentValues.map(v => ({ ...v, field_id: upgrade(v.field_id) })));
    }

    // Upgrade row_group_sorts
    const currentSorts = linkedTableState.row_group_sorts;
    if (currentSorts.some(s => !isQualifiedFieldId(s.field_id) && bareToQualified.has(s.field_id))) {
      linkedTableState.setRowGroupSorts(currentSorts.map(s => ({ ...s, field_id: upgrade(s.field_id) })));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run once when availableFields first populate; ref guard prevents re-runs
  }, [availableFields]);

  // ============================================================================
  // Version Data - For version selection (fetched directly, no syncing)
  // ============================================================================

  useLinkedTableVersionsQuery({
    linkedTable: linkedTableState.linkedTable,
    relationships: linkedTableState.relationships,
    isCreateMode,
  });

  // ============================================================================
  // LinkedTable Editor (unified state, handlers, and change tracking)
  // ============================================================================

  /**
   * LinkedTable editor configuration
   * Note: useLinkedTableEditor internally creates prelinkedTableConfig with only complete items
   */
  const linkedTableEditorConfig = React.useMemo(
    () => ({
      filters: linkedTableState.filters,
      calculated_columns: linkedTableState.calculated_columns,
      relationships: linkedTableState.relationships,
      versionSelections: linkedTableState.versionSelections,
      row_groups: linkedTableState.row_groups,
      row_group_sorts: linkedTableState.row_group_sorts,
      values: linkedTableState.values,
      variables: linkedTableState.variables,
    }),
    [
      linkedTableState.filters,
      linkedTableState.calculated_columns,
      linkedTableState.relationships,
      linkedTableState.versionSelections,
      linkedTableState.row_groups,
      linkedTableState.row_group_sorts,
      linkedTableState.values,
      linkedTableState.variables,
    ]
  );

  const linkedTableEditor = useLinkedTableEditor({
    config: linkedTableEditorConfig,
    setFilters: linkedTableState.setFilters,
    setCalculatedColumns: linkedTableState.setCalculatedColumns,
    setRelationships: linkedTableState.setRelationships,
    setVersionSelections: linkedTableState.setVersionSelections,
    setRowGroups: linkedTableState.setRowGroups,
    setRowGroupSorts: linkedTableState.setRowGroupSorts,
    setValues: linkedTableState.setValues,
    setVariables: linkedTableState.setVariables,
    tableFields: availableFields,
    tables: linkedTableState.linkedTable?.tables || [],
  });

  // Check if all items are complete (can save)
  const canSave = useMemo(() => {
    return (
      linkedTableEditor.incompleteCounts.filters === 0 &&
      linkedTableEditor.incompleteCounts.calculated_columns === 0 &&
      linkedTableEditor.incompleteCounts.relationships === 0
    );
  }, [linkedTableEditor.incompleteCounts]);

  const { handleSave } = useLinkedTableSaveMutation({
    isCreateMode,
    linkedTable: linkedTableState.linkedTable,
    linkedTableName: linkedTableState.linkedTableName,
    linkedTableDescription: linkedTableState.linkedTableDescription,
    filters: linkedTableState.filters,
    calculated_columns: linkedTableState.calculated_columns,
    relationships: linkedTableState.relationships,
    row_groups: linkedTableState.row_groups,
    row_group_sorts: linkedTableState.row_group_sorts,
    values: linkedTableState.values,
    variables: linkedTableState.variables,
    setLinkedTable: linkedTableState.setLinkedTable,
    syncOriginalState: linkedTableState.syncOriginalState,
  });

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="absolute inset-0 top-0 flex overflow-hidden">
      {/* Main content area */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
        {/* Data Table */}
        <div className="min-h-0 flex-1">
          {linkedTableState.linkedTable?.id && (
            <LinkedTableDataDisplay
              linkedTableId={linkedTableState.linkedTable.id}
              calculated_columns={linkedTableState.calculated_columns}
              linkedTable={linkedTableState.linkedTable}
              filters={linkedTableState.filters}
              relationships={linkedTableState.relationships}
              variables={linkedTableState.variables}
              row_groups={linkedTableState.row_groups}
              row_group_sorts={linkedTableState.row_group_sorts}
              values={linkedTableState.values}
              versionSelections={linkedTableState.versionSelections}
              configKey={linkedTableEditor.prelinkedTableKey}
              onFieldsChange={handleFieldsChange}
              onConfigNormalized={linkedTableEditor.handleConfigNormalized}
              variant="flush"
            />
          )}
        </div>
      </div>

      {/* Configuration Sidebar (Tabbed) */}
      <LinkedTableSidebar
        canEdit={canEdit}
        linkedTableName={linkedTableState.linkedTableName}
        linkedTableDescription={linkedTableState.linkedTableDescription}
        filters={linkedTableState.filters}
        calculated_columns={linkedTableState.calculated_columns}
        relationships={linkedTableState.relationships}
        availableFields={availableFields}
        row_groups={linkedTableState.row_groups}
        row_group_sorts={linkedTableState.row_group_sorts}
        values={linkedTableState.values}
        currentLinkedTableId={linkedTableState.linkedTable?.id}
        currentTableId={primaryTableId}
        table_id={primaryTableId || ''}
        tables={linkedTableState.linkedTable?.tables || []}
        linkedTableId={linkedTableState.linkedTable?.id}
        pendingChangesCount={linkedTableState.pendingChangesCount}
        canSave={canSave}
        incompleteCounts={linkedTableEditor.incompleteCounts}
        onUpdateName={linkedTableState.setLinkedTableName}
        onUpdateDescription={linkedTableState.setLinkedTableDescription}
        onAddFilter={linkedTableEditor.handleAddFilter}
        onUpdateFilter={linkedTableEditor.handleUpdateFilter}
        onAddCalculatedColumn={linkedTableEditor.handleAddCalculatedColumn}
        onUpdateCalculatedColumn={linkedTableEditor.handleUpdateCalculatedColumn}
        onRemoveCalculatedColumn={linkedTableEditor.handleRemoveCalculatedColumn}
        onAddRelationship={linkedTableEditor.handleAddRelationship}
        onUpdateRelationship={linkedTableEditor.handleUpdateRelationship}
        onRemoveFilter={linkedTableEditor.handleRemoveFilter}
        onRemoveRelationship={linkedTableEditor.handleRemoveRelationship}
        versionSelections={linkedTableState.versionSelections}
        onUpdateVersionSelection={linkedTableEditor.handleUpdateVersionSelection}
        variables={linkedTableState.variables}
        onAddVariable={linkedTableEditor.handleAddVariable}
        onUpdateVariable={linkedTableEditor.handleUpdateVariable}
        onRemoveVariable={linkedTableEditor.handleRemoveVariable}
        onAddToRowGroups={linkedTableEditor.handleAddToRowGroups}
        onRemoveFromRowGroups={linkedTableEditor.handleRemoveFromRowGroups}
        onReorderRowGroups={linkedTableEditor.handleReorderRowGroups}
        onUpdateRowGroupSort={linkedTableEditor.handleUpdateRowGroupSort}
        onAddToValues={linkedTableEditor.handleAddToValues}
        onRemoveFromValues={linkedTableEditor.handleRemoveFromValues}
        onReorderValues={linkedTableEditor.handleReorderValues}
        onUpdateValueFunction={linkedTableEditor.handleUpdateValueFunction}
        onSave={handleSave}
        onCancel={linkedTableState.resetToOriginal}
      />
    </div>
  );
};

export default EditLinkedTableContent;
